import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ErrorView from 'components/Error/ErrorView';
import Footer from 'components/Footer/Footer';
import Header from 'components/Header/Header';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  requestExternalData,
  sendDataToBase,
  useFetch,
} from 'services/APIClient';
import {
  DELIVERECT_API_BASE_URL,
  DELIVERECT_STORE_INFO_DETAIL_URL,
  DELIVERECT_STORE_INFO_URL,
  STORE_URL,
} from 'services/APIConstants';
import Interceptor from 'services/Interceptor';
import AuthToken, { isLoggedIn } from 'services/Token';

const DELIVERECT_DAYS_IN_WEEK = [
  { name: 'Mon', value: 1 },
  { name: 'Tue', value: 2 },
  { name: 'Wed', value: 3 },
  { name: 'Thu', value: 4 },
  { name: 'Fri', value: 5 },
  { name: 'Sat', value: 6 },
  { name: 'Sun', value: 7 },
];

// Section wrapper: gives every card the same "floating label" outline treatment
// used previously only for Working Schedules, so the page reads as one system.
const SectionCard = ({ label, icon, children, sx = {} }) => (
  <Paper
    variant="outlined"
    sx={{
      padding: '20px',
      marginBottom: '20px',
      position: 'relative',
      borderRadius: '10px',
      borderColor: '#e3e3e6',
      ...sx,
    }}
  >
    {label && (
      <Box
        sx={{
          position: 'absolute',
          top: '-13px',
          left: '2%',
          px: 1,
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {icon}
        <Typography
          sx={{
            color: '#6c6d74',
            fontSize: '1rem',
            fontFamily: 'Poppins',
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
      </Box>
    )}
    {children}
  </Paper>
);

const DeliverectStore = () => {
  const { uuid, accountId } = useParams();
  const [storeInfo, setStoreInfo] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [selectedAccountId, setSelectedAccountId] = useState(accountId || '');
  const [deliverectPayload, setDeliverectPayload] = useState(null);
  const [loadingDeliverect, setLoadingDeliverect] = useState(true);
  useEffect(() => {
    setSelectedAccountId(accountId || '');
  }, [accountId]);
  const [selectedWorkingDays, setSelectedWorkingDays] = useState([]);
  const [selectedWorkingHours, setSelectedWorkingHours] = useState([]);
  const [isSavingHours, setIsSavingHours] = useState(false);
  const [saveState, setSaveState] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const { isFetching, data, error } = useFetch(
    STORE_URL(uuid),
    AuthToken(),
    isLoggedIn() ? Interceptor : null
  );

  useEffect(() => {
    let ignore = false;

    const loadDeliverectData = async () => {
      setLoadingDeliverect(true);
      try {
        const payload = await requestExternalData(
          DELIVERECT_STORE_INFO_URL(uuid),
          AuthToken(),
          isLoggedIn() ? Interceptor : null
        );
        if (!ignore) {
          setDeliverectPayload(payload);
        }
      } catch (err) {
        if (!ignore) {
          setDeliverectPayload({ count: 0, results: [] });
        }
      } finally {
        if (!ignore) {
          setLoadingDeliverect(false);
        }
      }
    };

    loadDeliverectData();

    return () => {
      ignore = true;
    };
  }, [uuid]);

  const deliverectResults = useMemo(
    () => deliverectPayload?.results || [],
    [deliverectPayload]
  );

  const getAccountId = (entry) =>
    String(
      entry?.deliverect_account_id ||
        entry?.deliverect_account?.id ||
        entry?.id ||
        entry?.account_id ||
        ''
    );

  useEffect(() => {
    if (!deliverectResults.length) {
      return;
    }

    const exists = deliverectResults.some(
      (entry) => getAccountId(entry) === selectedAccountId
    );

    if (!exists) {
      setSelectedAccountId(getAccountId(deliverectResults[0]));
    }
  }, [deliverectResults, selectedAccountId]);

  const selectedDeliverectEntry = useMemo(
    () =>
      deliverectResults.find(
        (entry) => getAccountId(entry) === selectedAccountId
      ) || null,
    [deliverectResults, selectedAccountId]
  );

  const mapDeliverectToStoreFields = (entry) => {
    const info = entry?.deliverect_store_info || entry;

    if (!info) {
      return null;
    }

    const fullAddress = [info.house_number, info.street]
      .filter(Boolean)
      .join(' ')
      .trim();

    const openingHours = [...(info.opening_hours || [])].sort(
      (a, b) => Number(a.dayOfWeek) - Number(b.dayOfWeek)
    );
    const working_days = openingHours.map((item) => Number(item.dayOfWeek));
    const working_hours = openingHours.map((item) => {
      const start = dayjs(item.startTime, 'HH:mm').format('hh:mm A');
      const end = dayjs(item.endTime, 'HH:mm').format('hh:mm A');
      return `${start}-${end}`;
    });

    const channelLinksDetails = info.channel_links_details || {};
    const channelLabels = Object.values(channelLinksDetails)
      .map((channel) =>
        [channel?.name, channel?.channel].filter(Boolean).join(' - ')
      )
      .filter(Boolean);

    return {
      deliverect_store_info_id: info.id || '',
      name: info.name || '',
      alias: info.store_name || info.name || '',
      address: fullAddress,
      city: info.city || '',
      state: info.state_or_province || '',
      zip_code: info.postal_code || '',
      phone: info.phone || '',
      working_days,
      working_hours,
      is_enabled: true,
      deliverect_status: info.status || '',
      deliverect_timezone: info.timezone || '',
      deliverect_country: info.country || '',
      deliverect_region: info.region || '',
      deliverect_location_ref: info.deliverect_location_ref || '',
      deliverect_account: info.account || '',
      deliverect_pos_system_id: String(info.pos_system_id || ''),
      deliverect_tax_excl: String(Boolean(info.tax_excl)),
      deliverect_split_order_items: String(Boolean(info.split_order_items)),
      deliverect_updated_at: info.deliverect_updated_at || '',
      deliverect_synced_at: info.synced_at || '',
      // Kept as a joined string for backward compatibility (e.g. CSV export,
      // other consumers of storeInfo); the UI reads deliverect_channels_list.
      deliverect_channels: channelLabels.join(', '),
      deliverect_channels_list: channelLabels,
    };
  };

  const renderInfoField = (label, value) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        height: '100%',
        backgroundColor: '#fafafa',
        borderRadius: '8px',
      }}
    >
      <Typography
        variant="caption"
        sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="body1">{value || 'N/A'}</Typography>
    </Paper>
  );

  const buildDeliverectOpeningHours = (workingDays, workingHours) =>
    workingDays.map((dayOfWeek, index) => {
      const [startTime = '09:00 AM', endTime = '05:00 PM'] = (
        workingHours[index] || ''
      ).split('-');

      return {
        dayOfWeek: Number(dayOfWeek),
        startTime: dayjs(startTime, 'hh:mm A').format('HH:mm'),
        endTime: dayjs(endTime, 'hh:mm A').format('HH:mm'),
      };
    });

  const updateDeliverectWorkingHours = async (workingDays, workingHours) => {
    const deliverectStoreId =
      storeInfo?.deliverect_store_info_id || selectedDeliverectEntry?.id;

    if (!deliverectStoreId) {
      return;
    }

    if (!workingDays.length || !workingHours.length) {
      setFieldErrors({
        working_days: 'Set at least one working day with hours',
      });
      return;
    }

    setFieldErrors((prev) => ({ ...prev, working_days: undefined }));
    setIsSavingHours(true);

    try {
      await sendDataToBase(
        DELIVERECT_API_BASE_URL,
        DELIVERECT_STORE_INFO_DETAIL_URL(deliverectStoreId),
        {
          opening_hours: buildDeliverectOpeningHours(workingDays, workingHours),
        },
        AuthToken(),
        'patch',
        isLoggedIn() ? Interceptor : null,
        false
      );
      setSaveState({
        open: true,
        message: 'Working hours updated',
        severity: 'success',
      });
    } catch (err) {
      const errorStr = err?.data?.error || err?.message || 'Update failed';
      setSaveState({
        open: true,
        message: errorStr,
        severity: 'error',
      });
    } finally {
      setIsSavingHours(false);
    }
  };

  // Function to check if a specific day is selected
  const isDaySelected = (dayValue) => {
    return selectedWorkingDays.includes(dayValue);
  };

  // Function to return index of selected day
  const getSelectedDayIndex = (dayValue) => {
    return selectedWorkingDays.indexOf(dayValue);
  };

  // Function to toggle the selection of a working day
  const handleWorkingDayToggle = (dayValue) => {
    const updatedSelectedWorkingDays = selectedWorkingDays.length
      ? [...selectedWorkingDays]
      : [];
    const updatedWorkingHours = selectedWorkingHours.length
      ? [...selectedWorkingHours]
      : [];

    const index = updatedSelectedWorkingDays.indexOf(dayValue);
    if (index === -1) {
      updatedSelectedWorkingDays.push(dayValue);
      updatedWorkingHours.push('09:00 AM-05:00 PM');
    } else {
      updatedSelectedWorkingDays.splice(index, 1);
      updatedWorkingHours.splice(index, 1);
    }

    setSelectedWorkingDays(updatedSelectedWorkingDays);
    setSelectedWorkingHours(updatedWorkingHours);
    updateDeliverectWorkingHours(
      updatedSelectedWorkingDays,
      updatedWorkingHours
    );
  };

  // Function to handle changes in working hours
  const handleWorkingHoursChange = (dayIndex, timeType, value) => {
    if (!value) {
      return;
    }

    const updatedWorkingHours = [...selectedWorkingHours];
    const currentValue = updatedWorkingHours[dayIndex] || '09:00 AM-05:00 PM';
    const timeString = currentValue.split('-');
    let [startTime, endTime] = timeString;

    if (timeType === 'start') {
      startTime = value.format('hh:mm A');
    } else if (timeType === 'end') {
      endTime = value.format('hh:mm A');
    }

    updatedWorkingHours[dayIndex] = `${startTime}-${endTime}`;
    setSelectedWorkingHours(updatedWorkingHours);
    updateDeliverectWorkingHours(selectedWorkingDays, updatedWorkingHours);
  };

  useEffect(() => {
    if (!data?.results?.length) {
      return;
    }

    const baseStore = data.results[0];
    const mapped = mapDeliverectToStoreFields(selectedDeliverectEntry);

    if (!mapped) {
      setStoreInfo({
        ...baseStore,
        working_days: baseStore.working_days || [],
        working_hours: baseStore.working_hours || [],
      });
      setSelectedWorkingDays(
        baseStore.working_days !== 'NA' && Array.isArray(baseStore.working_days)
          ? baseStore.working_days
          : []
      );
      setSelectedWorkingHours(
        baseStore.working_hours !== 'NA' &&
          Array.isArray(baseStore.working_hours)
          ? baseStore.working_hours
          : []
      );
      return;
    }

    setStoreInfo({
      ...baseStore,
      ...mapped,
    });
    setSelectedWorkingDays(mapped.working_days || []);
    setSelectedWorkingHours(mapped.working_hours || []);
  }, [data, selectedDeliverectEntry]);

  if (isFetching || loadingDeliverect || !storeInfo) {
    return (
      <Typography variant="h6" align="top" mt={'0%'}>
        <LinearProgress />
      </Typography>
    );
  }

  if (error) {
    return <ErrorView statusCode={error.code} />;
  }

  const channelChips = storeInfo.deliverect_channels_list?.length
    ? storeInfo.deliverect_channels_list
    : (storeInfo.deliverect_channels || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);

  return (
    <>
      <Header isLoggedIn={isLoggedIn()} />
      <Grid
        item
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: '80vh', marginTop: '4rem', p: '3rem' }}
      >
        {/* ---- 1. Identity header: store name + linked channels up front ---- */}
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 3,
            borderRadius: '12px',
            borderColor: '#e3e3e6',
            background: 'linear-gradient(135deg, #fbfbfb 0%, #f4f4f6 100%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '10px',
                  backgroundColor: '#eceef3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <StorefrontOutlinedIcon sx={{ color: '#5b5d6b' }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: 'Poppins',
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {storeInfo.name || 'N/A'}
                </Typography>
                {storeInfo.alias && storeInfo.alias !== storeInfo.name && (
                  <Typography variant="body2" color="text.secondary">
                    {storeInfo.alias}
                  </Typography>
                )}
              </Box>
            </Stack>
            {storeInfo.deliverect_status && (
              <Chip
                label={storeInfo.deliverect_status}
                size="small"
                sx={{
                  backgroundColor: '#e6f4ea',
                  color: '#1e7a34',
                  fontWeight: 600,
                  textTransform: 'capitalize',
                }}
              />
            )}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary', mb: 1 }}
          >
            Linked Channels
          </Typography>
          {channelChips.length ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {channelChips.map((channel, idx) => (
                <Chip
                  key={`${channel}-${idx}`}
                  label={channel}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#d0d2db', mb: 1 }}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No channels linked
            </Typography>
          )}
        </Paper>

        {/* ---- 2. Opening hours ---- */}
        <SectionCard label="Working Schedules">
          <FormGroup>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Working hours are saved automatically when changed.
              </Typography>
              {isSavingHours && <CircularProgress size={20} />}
            </Box>
            {fieldErrors.working_days && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {fieldErrors.working_days}
              </Typography>
            )}
            <Grid container spacing={2}>
              {DELIVERECT_DAYS_IN_WEEK.map((day) => (
                <Grid key={day.value} xs={12} sm={6} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isDaySelected(day.value)}
                        onChange={() => handleWorkingDayToggle(day.value)}
                      />
                    }
                    label={day.name}
                  />
                  {isDaySelected(day.value) && (
                    <Grid container spacing={1}>
                      <Grid xs={6} sx={{ mb: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            label="Start Time"
                            value={
                              selectedWorkingHours[
                                getSelectedDayIndex(day.value)
                              ]
                                ? dayjs(
                                    selectedWorkingHours[
                                      getSelectedDayIndex(day.value)
                                    ].split('-')[0],
                                    'hh:mm A'
                                  )
                                : null
                            }
                            onChange={(time) =>
                              handleWorkingHoursChange(
                                getSelectedDayIndex(day.value),
                                'start',
                                time
                              )
                            }
                            ampm
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid xs={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <TimePicker
                            label="End Time"
                            value={
                              selectedWorkingHours[
                                getSelectedDayIndex(day.value)
                              ]
                                ? dayjs(
                                    selectedWorkingHours[
                                      getSelectedDayIndex(day.value)
                                    ].split('-')[1],
                                    'hh:mm A'
                                  )
                                : null
                            }
                            onChange={(time) =>
                              handleWorkingHoursChange(
                                getSelectedDayIndex(day.value),
                                'end',
                                time
                              )
                            }
                            ampm
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              ))}
            </Grid>
          </FormGroup>
        </SectionCard>

        {/* ---- 3. Address & contact, grouped together ---- */}
        <SectionCard
          label="Address & Contact"
          icon={
            <LocationOnOutlinedIcon sx={{ fontSize: 18, color: '#6c6d74' }} />
          }
        >
          <Grid container spacing={2}>
            <Grid xs={12}>{renderInfoField('Address', storeInfo.address)}</Grid>
            <Grid xs={12} sm={4}>
              {renderInfoField('City', storeInfo.city)}
            </Grid>
            <Grid xs={6} sm={4}>
              {renderInfoField('State', storeInfo.state)}
            </Grid>
            <Grid xs={6} sm={4}>
              {renderInfoField('Zip Code', storeInfo.zip_code)}
            </Grid>
            <Grid xs={12}>{renderInfoField('Phone', storeInfo.phone)}</Grid>
          </Grid>
        </SectionCard>

        {/* ---- 4. Remaining Deliverect metadata, grouped together ---- */}
        <SectionCard
          label="Deliverect Details"
          icon={<InfoOutlinedIcon sx={{ fontSize: 18, color: '#6c6d74' }} />}
        >
          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              {renderInfoField(
                'Deliverect Timezone',
                storeInfo.deliverect_timezone
              )}
            </Grid>
            <Grid xs={12} md={6}>
              {renderInfoField('Country', storeInfo.deliverect_country)}
            </Grid>
            <Grid xs={12} md={6}>
              {renderInfoField('Region', storeInfo.deliverect_region)}
            </Grid>
            <Grid xs={12} md={6}>
              {renderInfoField(
                'Location Ref',
                storeInfo.deliverect_location_ref
              )}
            </Grid>
            <Grid xs={12} md={6}>
              {renderInfoField('Account', storeInfo.deliverect_account)}
            </Grid>
            <Grid xs={12} md={6}>
              {renderInfoField(
                'POS System ID',
                storeInfo.deliverect_pos_system_id
              )}
            </Grid>
            <Grid xs={12} md={6}>
              {renderInfoField('Tax Excluded', storeInfo.deliverect_tax_excl)}
            </Grid>
            <Grid xs={12} md={6}>
              {renderInfoField(
                'Split Order Items',
                storeInfo.deliverect_split_order_items
              )}
            </Grid>
            <Grid xs={12} md={6}>
              {renderInfoField(
                'Last Deliverect Update',
                storeInfo.deliverect_updated_at
              )}
            </Grid>
            <Grid xs={12} md={6}>
              {renderInfoField('Last Synced', storeInfo.deliverect_synced_at)}
            </Grid>
          </Grid>
        </SectionCard>
      </Grid>
      <Footer />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={saveState.open}
        autoHideDuration={3000}
        onClose={() => setSaveState((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={saveState.severity}>{saveState.message}</Alert>
      </Snackbar>
    </>
  );
};

export default DeliverectStore;
